import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  InputAdornment,
  Typography,
  Divider,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { LoanProduct } from '../../api/types/loan.types';
import { useCreateLoanProduct, useUpdateLoanProduct } from '../../hooks/loans/useLoans';
import { useSnackbar } from 'notistack';

interface LoanProductDialogProps {
  open: boolean;
  onClose: () => void;
  product?: LoanProduct | null;
}

interface FormData {
  productName: string;
  productCode: string;
  description: string;
  minAmount: string;
  maxAmount: string;
  interestRate1Week: string;
  interestRate2Weeks: string;
  interestRate1Month: string;
  processingFeePercentage: string;
  latePaymentPenaltyPercentage: string;
  firstTimeBorrowerLimit: string;
  limitIncreasePercentage: string;
  limitIncreaseAfterLoans: string;
  processingFeeUpfront: boolean;
}

const initialFormData: FormData = {
  productName: '',
  productCode: '',
  description: '',
  minAmount: '',
  maxAmount: '',
  interestRate1Week: '5.00',
  interestRate2Weeks: '7.00',
  interestRate1Month: '10.00',
  processingFeePercentage: '2.00',
  latePaymentPenaltyPercentage: '2.50',
  firstTimeBorrowerLimit: '5000.00',
  limitIncreasePercentage: '20.00',
  limitIncreaseAfterLoans: '3',
  processingFeeUpfront: false,
};

const LoanProductDialog: React.FC<LoanProductDialogProps> = ({ open, onClose, product }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const isEditMode = !!product;

  const createMutation = useCreateLoanProduct();
  const updateMutation = useUpdateLoanProduct();

  useEffect(() => {
    if (product) {
      setFormData({
        productName: product.productName || '',
        productCode: product.productCode || '',
        description: product.description || '',
        minAmount: product.minAmount || '',
        maxAmount: product.maxAmount || '',
        interestRate1Week: product.interestRate1Week || '5.00',
        interestRate2Weeks: product.interestRate2Weeks || '7.00',
        interestRate1Month: product.interestRate1Month || '10.00',
        processingFeePercentage: product.processingFeePercentage || '2.00',
        latePaymentPenaltyPercentage: product.latePaymentPenaltyPercentage || '2.50',
        firstTimeBorrowerLimit: product.firstTimeBorrowerLimit || '5000.00',
        limitIncreasePercentage: product.limitIncreasePercentage || '20.00',
        limitIncreaseAfterLoans: product.limitIncreaseAfterLoans?.toString() || '3',
        processingFeeUpfront: product.processingFeeUpfront ?? false,
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
  }, [product, open]);

  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.productName.trim()) {
      newErrors.productName = 'Product name is required';
    }

    if (!formData.productCode.trim()) {
      newErrors.productCode = 'Product code is required';
    }

    const minAmount = parseFloat(formData.minAmount);
    const maxAmount = parseFloat(formData.maxAmount);

    if (!formData.minAmount || isNaN(minAmount) || minAmount <= 0) {
      newErrors.minAmount = 'Valid minimum amount is required';
    }

    if (!formData.maxAmount || isNaN(maxAmount) || maxAmount <= 0) {
      newErrors.maxAmount = 'Valid maximum amount is required';
    }

    if (minAmount && maxAmount && minAmount >= maxAmount) {
      newErrors.maxAmount = 'Maximum amount must be greater than minimum amount';
    }

    const rate1Week = parseFloat(formData.interestRate1Week);
    const rate2Weeks = parseFloat(formData.interestRate2Weeks);
    const rate1Month = parseFloat(formData.interestRate1Month);

    if (!formData.interestRate1Week || isNaN(rate1Week) || rate1Week < 0 || rate1Week > 100) {
      newErrors.interestRate1Week = 'Valid interest rate (0-100) is required';
    }

    if (!formData.interestRate2Weeks || isNaN(rate2Weeks) || rate2Weeks < 0 || rate2Weeks > 100) {
      newErrors.interestRate2Weeks = 'Valid interest rate (0-100) is required';
    }

    if (!formData.interestRate1Month || isNaN(rate1Month) || rate1Month < 0 || rate1Month > 100) {
      newErrors.interestRate1Month = 'Valid interest rate (0-100) is required';
    }

    const processingFee = parseFloat(formData.processingFeePercentage);
    if (!formData.processingFeePercentage || isNaN(processingFee) || processingFee < 0 || processingFee > 100) {
      newErrors.processingFeePercentage = 'Valid processing fee (0-100) is required';
    }

    const penaltyRate = parseFloat(formData.latePaymentPenaltyPercentage);
    if (!formData.latePaymentPenaltyPercentage || isNaN(penaltyRate) || penaltyRate < 0 || penaltyRate > 100) {
      newErrors.latePaymentPenaltyPercentage = 'Valid penalty rate (0-100) is required';
    }

    const borrowerLimit = parseFloat(formData.firstTimeBorrowerLimit);
    if (!formData.firstTimeBorrowerLimit || isNaN(borrowerLimit) || borrowerLimit <= 0) {
      newErrors.firstTimeBorrowerLimit = 'Valid borrower limit is required';
    }

    const limitIncrease = parseFloat(formData.limitIncreasePercentage);
    if (!formData.limitIncreasePercentage || isNaN(limitIncrease) || limitIncrease < 0 || limitIncrease > 100) {
      newErrors.limitIncreasePercentage = 'Valid limit increase (0-100) is required';
    }

    const loansCount = parseInt(formData.limitIncreaseAfterLoans);
    if (!formData.limitIncreaseAfterLoans || isNaN(loansCount) || loansCount < 0) {
      newErrors.limitIncreaseAfterLoans = 'Valid number of loans is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const productData = {
      productName: formData.productName.trim(),
      productCode: formData.productCode.trim(),
      description: formData.description.trim(),
      minAmount: parseFloat(formData.minAmount).toFixed(2),
      maxAmount: parseFloat(formData.maxAmount).toFixed(2),
      interestRate1Week: parseFloat(formData.interestRate1Week).toFixed(2),
      interestRate2Weeks: parseFloat(formData.interestRate2Weeks).toFixed(2),
      interestRate1Month: parseFloat(formData.interestRate1Month).toFixed(2),
      processingFeePercentage: parseFloat(formData.processingFeePercentage).toFixed(2),
      latePaymentPenaltyPercentage: parseFloat(formData.latePaymentPenaltyPercentage).toFixed(2),
      firstTimeBorrowerLimit: parseFloat(formData.firstTimeBorrowerLimit).toFixed(2),
      limitIncreasePercentage: parseFloat(formData.limitIncreasePercentage).toFixed(2),
      limitIncreaseAfterLoans: parseInt(formData.limitIncreaseAfterLoans),
      isActive: product?.isActive ?? true,
      processingFeeUpfront: formData.processingFeeUpfront,
    };

    try {
      if (isEditMode && product) {
        await updateMutation.mutateAsync({ productId: product.productId, data: productData });
        enqueueSnackbar('Loan product updated successfully', { variant: 'success' });
      } else {
        await createMutation.mutateAsync(productData);
        enqueueSnackbar('Loan product created successfully', { variant: 'success' });
      }
      onClose();
    } catch (error) {
      console.error('Failed to save product:', error);
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to save loan product',
        { variant: 'error' }
      );
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          {isEditMode ? 'Edit Loan Product' : 'Add New Loan Product'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error instanceof Error ? error.message : 'Failed to save loan product'}
          </Alert>
        )}
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Basic Information */}
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Product Name"
                fullWidth
                required
                value={formData.productName}
                onChange={handleChange('productName')}
                error={!!errors.productName}
                helperText={errors.productName}
                placeholder="e.g., Quick Cash"
              />
              <TextField
                label="Product Code"
                fullWidth
                required
                value={formData.productCode}
                onChange={handleChange('productCode')}
                error={!!errors.productCode}
                helperText={errors.productCode || 'Unique identifier'}
                placeholder="e.g., QL30"
                disabled={isEditMode}
              />
            </Box>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={handleChange('description')}
              error={!!errors.description}
              helperText={errors.description}
              placeholder="Describe the loan product..."
              sx={{ mt: 2 }}
            />
          </Box>

          {/* Loan Amounts */}
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Loan Amount Range
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Minimum Amount"
                fullWidth
                required
                type="number"
                value={formData.minAmount}
                onChange={handleChange('minAmount')}
                error={!!errors.minAmount}
                helperText={errors.minAmount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                }}
              />
              <TextField
                label="Maximum Amount"
                fullWidth
                required
                type="number"
                value={formData.maxAmount}
                onChange={handleChange('maxAmount')}
                error={!!errors.maxAmount}
                helperText={errors.maxAmount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                }}
              />
            </Box>
          </Box>

          {/* Interest Rates */}
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Interest Rates (by Tenor)
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
              <TextField
                label="1 Week Rate"
                fullWidth
                required
                type="number"
                value={formData.interestRate1Week}
                onChange={handleChange('interestRate1Week')}
                error={!!errors.interestRate1Week}
                helperText={errors.interestRate1Week}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
              <TextField
                label="2 Weeks Rate"
                fullWidth
                required
                type="number"
                value={formData.interestRate2Weeks}
                onChange={handleChange('interestRate2Weeks')}
                error={!!errors.interestRate2Weeks}
                helperText={errors.interestRate2Weeks}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
              <TextField
                label="1 Month Rate"
                fullWidth
                required
                type="number"
                value={formData.interestRate1Month}
                onChange={handleChange('interestRate1Month')}
                error={!!errors.interestRate1Month}
                helperText={errors.interestRate1Month}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Box>
          </Box>

          {/* Fees and Penalties */}
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Fees and Penalties
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Processing Fee"
                fullWidth
                required
                type="number"
                value={formData.processingFeePercentage}
                onChange={handleChange('processingFeePercentage')}
                error={!!errors.processingFeePercentage}
                helperText={errors.processingFeePercentage}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
              <TextField
                label="Late Payment Penalty"
                fullWidth
                required
                type="number"
                value={formData.latePaymentPenaltyPercentage}
                onChange={handleChange('latePaymentPenaltyPercentage')}
                error={!!errors.latePaymentPenaltyPercentage}
                helperText={errors.latePaymentPenaltyPercentage || 'Daily penalty rate for overdue loans'}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
              <Box sx={{ gridColumn: '1 / -1' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.processingFeeUpfront}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, processingFeeUpfront: e.target.checked }))
                      }
                      color="warning"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Collect processing fee upfront via M-Pesa
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        When enabled, an M-Pesa STK push is sent to the customer on application.
                        The application enters the review queue only after the fee is paid.
                        The customer then receives the full principal amount at disbursement.
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>
          </Box>

          {/* Borrower Limits */}
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Borrower Limits & Growth
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
              <TextField
                label="First-Time Borrower Limit"
                fullWidth
                required
                type="number"
                value={formData.firstTimeBorrowerLimit}
                onChange={handleChange('firstTimeBorrowerLimit')}
                error={!!errors.firstTimeBorrowerLimit}
                helperText={errors.firstTimeBorrowerLimit || 'Initial loan limit for new customers'}
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                }}
              />
              <TextField
                label="Limit Increase"
                fullWidth
                required
                type="number"
                value={formData.limitIncreasePercentage}
                onChange={handleChange('limitIncreasePercentage')}
                error={!!errors.limitIncreasePercentage}
                helperText={errors.limitIncreasePercentage || 'Percentage increase per milestone'}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
              <TextField
                label="After # of Loans"
                fullWidth
                required
                type="number"
                value={formData.limitIncreaseAfterLoans}
                onChange={handleChange('limitIncreaseAfterLoans')}
                error={!!errors.limitIncreaseAfterLoans}
                helperText={errors.limitIncreaseAfterLoans || 'Successful loans before increase'}
                InputProps={{
                  inputProps: { min: 0, step: 1 },
                }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit" disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isLoading}>
          {isLoading ? 'Saving...' : isEditMode ? 'Update Product' : 'Create Product'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoanProductDialog;
